import { useState } from 'react';
import { Check, Info } from 'lucide-react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { FOAM_BY_THICKNESS, FOAM_DENSITIES } from './cushionConstants';

const FOAM_IMAGE = 'https://images.unsplash.com/photo-1722356541555-eeabc38f80a8?w=400&q=80';

const WARRANTY_TEXT = 'Our indoor foams come with a 10-year manufacturer warranty. Cushions should always be kept indoors and away from rain. We recommend placing cushions out of prolonged direct sunlight to prevent colour fading, discolouration, and structural weakening of the material.';
const DACRON_NOTE = 'All our foam inserts are wrapped in a layer of premium 100gsm polyester dacron for added comfort and a plush finish. Please note this wrapping may add approximately 10mm (1cm) to the overall cushion thickness.';

const FoamInlineSection = ({ thicknessCm, selectedFoam, onFoamSelect, productType, showAllFoams = false }) => {
  const [showWarrantyTooltip, setShowWarrantyTooltip] = useState(false);

  // Don't render if product type doesn't need foam
  if (productType === 'cover_only') return null;
  // Don't render if no thickness selected (unless showAllFoams)
  if (!thicknessCm && !showAllFoams) return null;

  let availableFoams;
  if (showAllFoams) {
    availableFoams = Object.values(FOAM_DENSITIES);
  } else {
    const availableIds = FOAM_BY_THICKNESS[thicknessCm] || [];
    availableFoams = availableIds.map(id => FOAM_DENSITIES[id]).filter(Boolean);
  }

  if (availableFoams.length === 0) return null;

  return (
    <div className="col-span-2 sm:col-span-1 space-y-3 pt-4 border-t border-gray-200" data-testid="foam-inline-section">
      {/* Heading with warranty tooltip */}
      <div className="flex items-center gap-2 relative">
        <h3 className="text-sm sm:text-lg font-semibold text-[#1A1A1A]">Foam Density</h3>
        <button
          type="button"
          className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-gray-200 text-gray-500 text-[10px] sm:text-xs font-semibold flex items-center justify-center flex-shrink-0 cursor-pointer"
          onClick={() => setShowWarrantyTooltip(!showWarrantyTooltip)}
          onMouseEnter={() => setShowWarrantyTooltip(true)}
          onMouseLeave={() => setShowWarrantyTooltip(false)}
          data-testid="foam-warranty-tooltip-btn"
        >
          <Info className="h-3 w-3" />
        </button>
        {showWarrantyTooltip && (
          <div className="absolute left-0 bottom-full mb-2 z-50 w-72 sm:w-80 p-3 bg-gray-900 text-white text-[11px] sm:text-xs rounded-lg shadow-lg leading-relaxed" data-testid="foam-warranty-tooltip">
            <div className="absolute left-8 top-full w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[6px] border-t-gray-900"></div>
            {WARRANTY_TEXT}
          </div>
        )}
      </div>
      <p className="text-xs sm:text-sm text-gray-500">Select the foam density for your cushion.</p>

      {/* Foam options */}
      <RadioGroup value={selectedFoam || ''} onValueChange={onFoamSelect} className="space-y-2.5">
        {availableFoams.map((foam) => (
          <label
            key={foam.id}
            className={`relative flex items-start gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all ${
              selectedFoam === foam.id ? 'border-bush bg-bush/5' : 'border-gray-200 hover:border-gray-300'
            }`}
            data-testid={`foam-option-${foam.id}`}
          >
            <RadioGroupItem value={foam.id} className="sr-only" />
            {/* Foam image */}
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
              <img src={FOAM_IMAGE} alt="Foam" className="w-full h-full object-cover" loading="lazy" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="font-semibold text-sm sm:text-base text-[#1A1A1A]">{foam.name}</span>
              <p className="text-xs sm:text-sm text-gray-500 mt-0.5 leading-relaxed">{foam.description}</p>
            </div>
            {selectedFoam === foam.id && (
              <div className="w-5 h-5 sm:w-6 sm:h-6 bg-bush text-white rounded-full flex items-center justify-center ml-1 flex-shrink-0 mt-1">
                <Check className="h-3 w-3 sm:h-4 sm:w-4" />
              </div>
            )}
          </label>
        ))}
      </RadioGroup>

      {/* Dacron note */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 sm:p-4 mt-3" data-testid="dacron-note">
        <p className="text-xs sm:text-sm text-amber-800 leading-relaxed">
          <span className="font-semibold">Note:</span> {DACRON_NOTE}
        </p>
      </div>
    </div>
  );
};

export default FoamInlineSection;
